import { CardContentRenderer } from './CardContentRenderer';
import { cn } from '@/utils';
import React, { useMemo } from 'react';
import type { ISimulationPlanningCard } from '@/types';

interface Props {
  /** The currently selected planning card that should be treated as "active". */
  activeCard: ISimulationPlanningCard | null;
  /** Horizontal spacing between cards (in rem) so transform aligns with card gaps. */
  horizontalPaddingRem: number;
  /** Modal open state used to control transition timing behavior. */
  isOpen: boolean;
  /** Zero-based index of the active card after normalization. This drives horizontal transforms. */
  normalizedActiveCardIndex: number;
  /** Full card collection rendered into the carousel track. */
  planningCards: ISimulationPlanningCard[];
}

/**
 * ContentCarousel
 *
 * Renders planning cards as a horizontal carousel track.
 *
 * Behavior summary:
 * 1. Compute a translateX value from the active card index.
 * 2. Apply track-level transform animation for sliding between cards.
 * 3. Keep all cards mounted, but visually de-emphasize non-active cards.
 *
 * Keeping cards mounted helps preserve internal layout and avoids mount/unmount
 * flicker when stepping through planning content.
 */
export const ContentCarousel: React.FC<Props> = ({
  activeCard,
  horizontalPaddingRem,
  isOpen,
  normalizedActiveCardIndex,
  planningCards,
}) => {
  // ==================================
  // Memoized Values
  // ==================================

  /**
   * Calculate the track transform value based on the active card index and horizontal padding.
   * This moves the track left/right to bring the active card into the viewport.
   * The calculation accounts for both card width (100%) and horizontal gaps (padding).
   */
  const transformValue = useMemo(() => {
    const index = normalizedActiveCardIndex;
    const padding = horizontalPaddingRem;
    return `translateX(calc(-${index * 100}% - ${index * padding}rem))`;
  }, [normalizedActiveCardIndex, horizontalPaddingRem]);

  // ==================================
  // Class Names
  // ==================================

  // Track classes control sliding animation and spacing for the entire row.
  const classNames = cn([
    'flex',
    'ease-[cubic-bezier(0.4,0,0.2,1)]',
    'w-full',
    'gap-6',
    'transition-transform',
    'delay-75',
    'duration-300',
    'will-change-transform',
  ]);

  // Base card classes are shared by active and inactive cards.
  // Per-card opacity and duration variants are added in the map below.
  const baseCardClassNames = cn([
    'duration-100',
    'ease-in-out',
    'px-6',
    'shrink-0',
    'transition-all',
    'w-full',
    'will-change-opacity',
  ]);

  // ==================================
  // Render
  // ==================================
  return (
    // The outer wrapper clips overflow so only one viewport slice is visible.
    <div className="overflow-hidden">
      {/*
        Track element that moves left/right as the active step changes.
        Inline style is intentional here because transform is dynamic.
      */}
      <div className={classNames} style={{ transform: transformValue }}>
        {planningCards.map((card) => (
          <div
            key={card.stepNumber}
            // Hide non-active cards from assistive tech while
            // still keeping them mounted.
            aria-hidden={card.stepNumber !== activeCard?.stepNumber}
            className={cn(baseCardClassNames, {
              // When modal is closed, disable transition duration
              // to prevent close animation lag.
              'duration-0': !isOpen,

              // Active card is fully visible.
              'opacity-100': card.stepNumber === activeCard?.stepNumber,

              // Inactive cards remain rendered but dimmed.
              'opacity-45': card.stepNumber !== activeCard?.stepNumber,
            })}
          >
            {/* 
              Delegate card body rendering so carousel remains
              focused on layout/animation.
            */}
            <CardContentRenderer card={card} />
          </div>
        ))}
      </div>
    </div>
  );
};

ContentCarousel.displayName = 'ContentCarousel';
